import { Box, Divider, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useEntity } from '@backstage/plugin-catalog-react';
import { countFilledLeadershipSlots } from '../shared/leadershipSlots';
import {
  type UseTeamMemberEntitiesResult,
  useTeamMemberEntities,
} from '../shared/useTeamMemberEntities';

const MEMBER_FIELDS = ['kind', 'metadata.name'];

const useStyles = makeStyles(theme => ({
  divider: {
    marginTop: theme.spacing(1),
  },
}));

export interface EntityTeamRosterHeaderProps {
  /** Pre-fetched member data to reuse instead of issuing a second
   * `getEntitiesByRefs` request — passed by `EntityTeamRosterSection`, which
   * fetches once and shares the result with `EntityTeamMembersCard`. When
   * omitted (e.g. the header is rendered standalone), it fetches for itself. */
  members?: UseTeamMemberEntitiesResult;
}

/** Full-width header above the Leadership and Members cards, showing the
 * combined "Team (N)" total so leads/architects (counted in the Leadership
 * card, not the Members card) aren't left feeling uncounted. */
export function EntityTeamRosterHeader({ members }: EntityTeamRosterHeaderProps = {}) {
  const classes = useStyles();
  const { entity } = useEntity();
  const ownFetch = useTeamMemberEntities(MEMBER_FIELDS, { enabled: !members });
  const { items, loading, error } = members ?? ownFetch;

  const leadershipSlotCount = countFilledLeadershipSlots(entity);
  const total = leadershipSlotCount + items.length;

  return (
    <Box>
      <Typography variant="h5">
        {loading || error ? 'Team' : `Team (${total})`}
      </Typography>
      <Typography variant="caption" color="textSecondary">
        Leaders and members together make up the full team.
      </Typography>
      <Divider className={classes.divider} />
    </Box>
  );
}
